import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Camera, ArrowRight, GitCompareArrows } from "lucide-react";
import { useLang } from "@/context";
import { T, linkTo, formatPrice, formatMGA, formatSurface } from "@/i18n";
import { ZONES } from "@/data/zones";

export const StatusBadge = ({ status }) => {
  const { lang } = useLang();
  const t = T[lang];
  const map = {
    available: ["bg-[#2FA3A0]", t.common.available],
    reserved: ["bg-[#E8B64C] !text-[#1F2328]", t.common.reserved],
    sold: ["bg-[#8A8F96]", t.common.sold],
  };
  const [cls, label] = map[status] || map.available;
  return <span className={`${cls} text-white text-[11px] font-semibold uppercase tracking-wider rounded-full px-2.5 py-1`} data-testid={`status-badge-${status}`}>{label}</span>;
};

export const PhotoPlaceholder = ({ className = "", label }) => {
  const { lang } = useLang();
  const t = T[lang];
  return (
    <div className={`photo-placeholder relative flex flex-col items-center justify-center text-white/90 gap-2 ${className}`} data-testid="photo-placeholder">
      <Camera size={28} strokeWidth={1.5} />
      <span className="text-xs font-semibold uppercase tracking-[0.18em]">{label || t.common.photosComingSoon}</span>
    </div>
  );
};

export function PropertyCard({ item, isFav, onFav, inCompare, onCompare }) {
  const { lang } = useLang();
  const t = T[lang];
  const zone = ZONES.find((z) => z.slug === item.zone);
  return (
    <article className={`card-site overflow-hidden group ${item.status === "sold" ? "opacity-75" : ""}`} data-testid={`property-card-${item.ref_code}`}>
      <Link to={linkTo(lang, "property", item.slug)} className="block relative">
        {item.images && item.images.length > 0 ? (
          <img src={item.images[0]} alt={item.title[lang]} className="h-56 w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
        ) : (
          <PhotoPlaceholder className="h-56 w-full transition-transform duration-700 group-hover:scale-105" />
        )}
        <div className="absolute top-3 left-3 flex gap-2">
          <StatusBadge status={item.status} />
          {item.demo && <span className="bg-[#E8B64C] text-[#1F2328] text-[11px] font-semibold uppercase tracking-wider rounded-full px-2.5 py-1" data-testid="demo-badge">{t.common.example}</span>}
          {item.featured && <span className="bg-[#F6EFE4]/90 text-[#B5472B] text-[11px] font-semibold uppercase tracking-wider rounded-full px-2.5 py-1">{t.common.featured}</span>}
        </div>
        {onFav && (
          <button
            onClick={(e) => { e.preventDefault(); onFav(item); }}
            aria-label={t.common.favorites} data-testid={`fav-btn-${item.ref_code}`}
            className={`absolute top-3 right-3 h-9 w-9 rounded-full flex items-center justify-center backdrop-blur transition-colors ${isFav ? "bg-[#B5472B] text-white" : "bg-white/80 text-[#0E4D5C] hover:bg-white"}`}
          >
            <Heart size={16} fill={isFav ? "currentColor" : "none"} />
          </button>
        )}
      </Link>
      <div className="p-5">
        <div className="flex items-center justify-between text-xs text-[#8A8F96] font-medium uppercase tracking-wider">
          <span>{t.types[item.type]}</span>
          <span data-testid={`ref-${item.ref_code}`}>{item.ref_code}</span>
        </div>
        <Link to={linkTo(lang, "property", item.slug)}>
          <h3 className="font-serif text-lg font-semibold text-[#1F2328] mt-2 leading-snug group-hover:text-[#B5472B] transition-colors">{item.title[lang]}</h3>
        </Link>
        <p className="text-sm text-[#1F2328]/60 mt-1">{zone ? zone.name : item.zone} · {formatSurface(item.surface_m2, lang)}</p>
        <div className="mt-4 flex items-end justify-between">
          <div>
            <p className="font-serif text-xl text-[#0E4D5C]" data-testid={`price-${item.ref_code}`}>
              {item.price_on_request ? t.common.priceOnRequest : formatPrice(item.price_eur, lang)}
            </p>
            {!item.price_on_request && <p className="text-xs text-[#8A8F96]">{formatMGA(item.price_eur, lang)}</p>}
          </div>
          {onCompare && (
            <button onClick={() => onCompare(item)} data-testid={`compare-btn-${item.ref_code}`}
              className={`flex items-center gap-1.5 text-xs font-semibold rounded-full border px-3 py-1.5 transition-colors ${inCompare ? "bg-[#0E4D5C] text-white border-[#0E4D5C]" : "border-[#0E4D5C]/25 text-[#0E4D5C] hover:bg-[#0E4D5C] hover:text-white"}`}>
              <GitCompareArrows size={13} /> {t.common.compare}
            </button>
          )}
        </div>
      </div>
    </article>
  );
}
